import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { composeAddress, makeApi, type AdminAddress, type AdminProject } from '../api/client';

export function AdminAddressesPage(): JSX.Element {
  const { getToken } = useAuth();
  const api = useMemo(() => makeApi(getToken), [getToken]);
  const [addresses, setAddresses] = useState<AdminAddress[]>([]);
  const [projects, setProjects] = useState<AdminProject[]>([]);
  const [search, setSearch] = useState('');
  const [projectId, setProjectId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [showNew, setShowNew] = useState(false);
  const [streetAddress, setStreetAddress] = useState('');
  const [suburb, setSuburb] = useState('');
  const [state, setState] = useState('');
  const [postcode, setPostcode] = useState('');
  const [busy, setBusy] = useState(false);

  const [attachPid, setAttachPid] = useState<Record<string, string>>({});

  useEffect(() => {
    api.adminListProjects().then((r) => setProjects(r.projects)).catch(() => {});
  }, []);

  async function load(): Promise<void> {
    setLoading(true);
    try {
      const r = await api.adminListAddresses({
        search: search || undefined,
        projectId: projectId || undefined,
      });
      setAddresses(r.addresses);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [projectId]);

  async function onCreate(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.adminCreateAddress({ streetAddress, suburb, state, postcode });
      setStreetAddress('');
      setSuburb('');
      setState('');
      setPostcode('');
      setShowNew(false);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create');
    } finally {
      setBusy(false);
    }
  }

  async function onAttach(addressId: string): Promise<void> {
    const pid = attachPid[addressId];
    if (!pid) return;
    setBusy(true);
    try {
      await api.adminAttachAddress(addressId, pid);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Attach failed');
    } finally {
      setBusy(false);
    }
  }

  async function onDetach(addressId: string, pid: string): Promise<void> {
    if (!confirm('Detach this address from the project?')) return;
    setBusy(true);
    try {
      await api.adminDetachAddress(addressId, pid);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Detach failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 items-end">
        <div className="flex-1 min-w-[240px]">
          <label className="label">Search</label>
          <input
            className="input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && load()}
            placeholder="street/suburb/postcode"
          />
        </div>
        <div>
          <label className="label">Filter project</label>
          <select className="input" value={projectId} onChange={(e) => setProjectId(e.target.value)}>
            <option value="">All</option>
            {projects.map((p) => (
              <option key={p.pid} value={p.pid}>
                {p.projectReferenceNumber}
              </option>
            ))}
          </select>
        </div>
        <button className="btn-primary" onClick={() => setShowNew(!showNew)}>
          {showNew ? 'Cancel' : 'New address'}
        </button>
      </div>

      {showNew && (
        <form onSubmit={onCreate} className="card grid gap-3 sm:grid-cols-4">
          <div className="sm:col-span-2">
            <label className="label">Street address</label>
            <input className="input" value={streetAddress} onChange={(e) => setStreetAddress(e.target.value)} required />
          </div>
          <div>
            <label className="label">Suburb</label>
            <input className="input" value={suburb} onChange={(e) => setSuburb(e.target.value)} required />
          </div>
          <div>
            <label className="label">State</label>
            <input className="input" value={state} onChange={(e) => setState(e.target.value)} required />
          </div>
          <div>
            <label className="label">Postcode</label>
            <input className="input" value={postcode} onChange={(e) => setPostcode(e.target.value)} required />
          </div>
          <div className="sm:col-span-4">
            <button className="btn-primary" disabled={busy}>
              {busy ? 'Creating…' : 'Create'}
            </button>
          </div>
        </form>
      )}

      {error && <div className="text-sm text-red-400">{error}</div>}
      {loading ? (
        <div className="text-gray-500">Loading…</div>
      ) : (
        <div className="overflow-x-auto card p-0">
          <table className="w-full text-sm">
            <thead className="bg-gray-800/60 text-left text-gray-400 border-b border-gray-700">
              <tr>
                <th className="p-3">Address</th>
                <th className="p-3">Projects</th>
                <th className="p-3">Attach</th>
              </tr>
            </thead>
            <tbody>
              {addresses.map((a) => (
                <tr key={a.addressId} className="border-t border-gray-800 hover:bg-gray-800/40 transition-colors">
                  <td className="p-3 text-gray-200">{composeAddress(a)}</td>
                  <td className="p-3 text-xs">
                    {a.projectSummary.length === 0 && <span className="text-gray-600">—</span>}
                    <div className="flex flex-wrap gap-1">
                      {a.projectSummary.map((p) => (
                        <span
                          key={p.projectId}
                          className="badge bg-gray-800 text-gray-300 border border-gray-700 inline-flex items-center gap-1"
                        >
                          {p.projectReferenceNumber}
                          <button
                            className="text-red-400 hover:text-red-300"
                            onClick={() => onDetach(a.addressId, p.projectId)}
                            title="Detach"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="p-3">
                    <div className="flex gap-1">
                      <select
                        className="input text-xs"
                        value={attachPid[a.addressId] ?? ''}
                        onChange={(e) =>
                          setAttachPid((prev) => ({ ...prev, [a.addressId]: e.target.value }))
                        }
                      >
                        <option value="">Pick project</option>
                        {projects.map((p) => (
                          <option key={p.pid} value={p.pid}>
                            {p.projectReferenceNumber}
                          </option>
                        ))}
                      </select>
                      <button
                        className="btn-secondary text-xs"
                        disabled={busy || !attachPid[a.addressId]}
                        onClick={() => onAttach(a.addressId)}
                      >
                        Attach
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {addresses.length === 0 && (
                <tr>
                  <td colSpan={3} className="p-4 text-center text-gray-500">
                    No addresses
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
